/*!
 * Copyright 2019 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  Output,
} from "@angular/core";
import {ConfigFormDirective} from "@doctor-panel-modules/patient-treatment-plan/directives/config-form.directive";
import {ModalController} from "@ionic/angular";
import {DialogDeleteAppointmentsComponent} from "@modules/doctor-panel/components/dialog-delete-appointments/dialog-delete-appointments.component";
import {Store} from "@ngxs/store";
import {Appointment} from "@trec/api";
import {TrecError} from "@trec/api/errors";
import {C3SnackbarStatusService} from "@trec/components/snackbar-status";
import {Observable} from "rxjs";
import {ConfigTelemonitoringComponent} from "../config-telemonitoring/config-telemonitoring.component";

@Component({
  selector: "c3-config-section-box",
  templateUrl: "./config-section-box.component.html",
  styleUrls: ["./config-section-box.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ConfigSectionBoxComponent implements AfterContentInit {
  @Input()
  title: string;

  @Input()
  icon?: string;

  @Output() onSaveComplete = new EventEmitter<void>();

  touched$: Observable<boolean>;
  valid$: Observable<boolean>;
  loading$: Observable<boolean>;

  hideSaveBtn = false;

  /**
   * Directive applied to the config-form box of every config section.
   */
  @ContentChild(ConfigFormDirective) configComp: ConfigFormDirective | undefined;

  constructor(private store: Store, private c3Statusbar: C3SnackbarStatusService,
    private modalCtrl: ModalController) { }

  ngAfterContentInit() {
    // Get config form component and extract status data from it
    if (this.configComp && this.configComp.configForm) {
      this.touched$ = this.configComp.configForm.touched$;
      this.valid$ = this.configComp.configForm.valid$;
      this.loading$ = this.configComp.configForm.loading$;

      this.hideSaveBtn = this.configComp.hideSaveBtn;
    }
  }

  onSave() {
    if (!this.configComp) {
      return;
    }
    try {
      const saveAction = this.configComp.configForm.prepareSaveDataAction();
      if (!saveAction) {
        return;
      }

      // Perform save actions and wait for result
      this.store.dispatch(saveAction).subscribe(
        () => {
          this.c3Statusbar.showSuccess({message: `Configurazioni salvate correttamente`});
          if (this.configComp) {
            this.configComp.configForm.resetTouched();

            // If telemonitoring is disabled check for appointments to delete
            if (localStorage.getItem('configuratoreAppointments') === 'true' && this.configComp.configForm instanceof ConfigTelemonitoringComponent) {
              if (!this.configComp.configForm.isTelemonitoringEnabled) {
                this.configComp.configForm.getAppointments().subscribe(appointments => {
                  if (appointments.length > 0) {
                    this.openDeleteAppointmentsDialog(appointments);
                  } else {
                    this.c3Statusbar.showSuccess({message: `Non sono stati trovati eventuali appuntamenti da disdire`});
                  }
                });
              }
            }

          }
          this.onSaveComplete.emit()
        },
        () => {
          // Handled by store action
        }
      );
    } catch (e) {
      this.c3Statusbar.showError({
        error: new TrecError("form-invalid-err", ``),
        messagesMap: {
          "form-invalid-err":
            "Sembrano esserci alcuni campi sbagliati. Per favore ricontrolla tutti i valori.",
        },
      });
      throw e;
    }
  }

  async openDeleteAppointmentsDialog(appointments: Appointment[]) {

    const modal = await this.modalCtrl.create({
      component: DialogDeleteAppointmentsComponent,
      cssClass: "c3-modal-width-sm c3-modal-height-lg",
      componentProps: {
        data: {
          appointments
        },
      },
    });
    return await modal.present();

  }

}
